// src/pages/Home.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
} from "@mui/material";

// Feature icons
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SportsRugbyIcon from "@mui/icons-material/SportsRugby";
import GroupsIcon from "@mui/icons-material/Groups";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";

// Glossary icons
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SportsFootballOutlinedIcon from "@mui/icons-material/SportsFootballOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

// Typed text (your fixed component)
import TypedText from "../components/anim/TypedText";

// New palette
const BRAND = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  border: "#86B6F6",
  accent: "#176B87",
  ink: "#0B2230",
  white: "#FFFFFF",
  soft: "rgba(23,107,135,0.08)",
};

type GlossaryItem = {
  term: string;
  desc: string;
  icon: React.ReactNode;
};

const features = [
  {
    title: "World Cup Insights",
    subtitle: "Over a century worth of data",
    body: "Track key world cup data through an interactive map.",
    icon: <InsightsOutlinedIcon sx={{ fontSize: 38, color: BRAND.accent, mb: 1 }} />,
  },
  {
    title: "Pattern Recognition",
    subtitle: "Reveal tactical behaviors",
    body: "Understand team structures throughout history using various visuals.",
    icon: <HubOutlinedIcon sx={{ fontSize: 38, color: BRAND.accent, mb: 1 }} />,
  },
  {
    title: "Team Comparison",
    subtitle: "Head-to-head breakdowns",
    body: "Compare teams across win ratios, consistency, and historical matchups.",
    icon: <SportsRugbyIcon sx={{ fontSize: 38, color: BRAND.accent, mb: 1 }} />,
  },
];

const ctaFeatures = [
  {
    title: "Teams",
    subtitle: "Competition between 10 teams",
    body: "Compare teams across win–loss ratios, consistency, and home vs. away analyses.",
    icon: <GroupsIcon sx={{ fontSize: 38, color: BRAND.accent, mb: 1 }} />,
  },
  {
    title: "Matches",
    subtitle: "Game Insights",
    body: "Track match outcomes, understand historical trends, and visualize overall performance.",
    icon: <ScoreboardIcon sx={{ fontSize: 38, color: BRAND.accent, mb: 1 }} />,
  },
];

// Rugby glossary (practical + common broadcast terms)
const glossary: GlossaryItem[] = [
  { term: "Try", desc: "Grounding the ball in the in-goal area. Worth 5 points.", icon: <EmojiEventsOutlinedIcon /> },
  { term: "Penalty Kick", desc: "Kick at goal after an infringement. Worth 3 points.", icon: <FlagOutlinedIcon /> },
  { term: "Forward Pass", desc: "Passing the ball forward (illegal). Leads to a scrum for the opposition.", icon: <SwapHorizOutlinedIcon /> },
  { term: "Ruck", desc: "Contest over the ball on the ground after a tackle, players stay on feet.", icon: <LeaderboardOutlinedIcon /> },
  { term: "Tackle", desc: "Bringing the ball-carrier to ground below the shoulders (lawful contact).", icon: <ShieldOutlinedIcon /> },
  { term: "Turnover", desc: "Change of possession, often via a steal at the ruck or a knock-on.", icon: <RestartAltOutlinedIcon /> },
  { term: "Points Conceded", desc: " For opponents with at least 3 games played, this ranks who scores the most against a specific team per match.", icon: <FlagOutlinedIcon /> },
  { term: "Game Difference (GD)", desc: "Try/points difference: GD = Points For − Points Against. Positive → you outscore them overall. Negative → they outscore you.", icon: <SportsFootballOutlinedIcon /> },
  { term: "Territory", desc: "Where the game is played on the field; often tracked by kicks/position.", icon: <StraightenOutlinedIcon /> },
  { term: "Possession", desc: "Time or share of ball control by a team.", icon: <SportsFootballOutlinedIcon /> },
  { term: "Margin", desc: "Points For minus Points Against (how much you win/lose by).", icon: <LeaderboardOutlinedIcon /> },
  { term: "Bonus Point", desc: "Competition-specific extra point (e.g., 4 tries or close loss).", icon: <EmojiEventsOutlinedIcon /> },
];

// --- Dataset intro (not literally using your dataset in code, but describing it accurately) ---
const DATASET_META = {
  startYear: 1871,
  endYear: 2022,
  coreColumns: [
    "date",
    "home_team",
    "away_team",
    "home_score",
    "away_score",
    "competition",
    "stadium",
    "city",
    "country",
    "neutral",
    "world_cup",
  ],
};

// A curated, non-exhaustive “example teams” list (purely for UI)
const DATASET_TEAMS_PREVIEW = [
  "New Zealand",
  "South Africa",
  "England",
  "Ireland",
  "France",
  "Wales",
  "Scotland",
  "Australia",
  "Argentina",
  "Italy",
];

const DATASET_SAMPLE = `date,home_team,away_team,home_score,away_score,competition,stadium,city,country,neutral,world_cup
1871-03-27,Scotland,England,1,0,1871 Scotland v England International,Raeburn Place,Edinburgh,Scotland,False,False
1872-02-05,England,Scotland,2,1,1871–72 Home Nations International,The Oval,London,England,False,False
1873-03-03,Scotland,England,0,0,1872–73 Home Nations International,West of Scotland F.C.,Glasgow,Scotland,False,False`;

const FeatureCard: React.FC<{
  title: string;
  subtitle: string;
  body: string;
  icon: React.ReactNode;
}> = ({ title, subtitle, body, icon }) => (
  <Card
    sx={{
      backgroundColor: BRAND.white,
      borderRadius: 3,
      border: `1px solid ${BRAND.border}`,
      minHeight: 260,
      display: "flex",
      flexDirection: "column",
      px: { xs: 2.5, sm: 3 },
      py: { xs: 2.5, sm: 3 },
      position: "relative",
      boxShadow: "0 10px 22px rgba(23,107,135,0.10)",
      transition: "transform 0.18s ease, box-shadow 0.18s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 14px 30px rgba(23,107,135,0.18)",
      },
      "&::before": {
        content: '""',
        display: "block",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "5px",
        backgroundColor: BRAND.accent,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
      },
    }}
  >
    <CardContent sx={{ p: 0, textAlign: "center" }}>
      {icon}
      <Typography variant="h6" fontWeight={900} color={BRAND.ink} fontFamily="Poppins" mb={0.8}>
        {title}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: "rgba(11,34,48,0.70)" }} mb={1.2}>
        {subtitle}
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.70)" }}>
        {body}
      </Typography>
    </CardContent>
  </Card>
);

const GlossaryCard: React.FC<GlossaryItem> = ({ term, desc, icon }) => (
  <Card
    sx={{
      backgroundColor: BRAND.white,
      borderRadius: 3,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      boxShadow: "0 10px 22px rgba(23,107,135,0.10)",
    }}
  >
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            backgroundColor: BRAND.panel,
            color: BRAND.accent,
            border: `1px solid ${BRAND.border}`,
            flex: "0 0 auto",
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 900, color: BRAND.ink }} fontFamily="Poppins">
          {term}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", lineHeight: 1.65 }}>
        {desc}
      </Typography>
    </CardContent>
  </Card>
);

/**
 * Simple animated counter (no extra deps).
 * - starts when it appears in viewport
 * - animates from 0 -> target
 */
const useInViewOnce = (threshold = 0.25) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, inView };
};

const AnimatedNumber: React.FC<{
  value: number;
  suffix?: string;
  durationMs?: number;
  decimals?: number;
  fontSize?: number | string;
}> = ({ value, suffix = "", durationMs = 900, decimals = 0, fontSize = 34 }) => {
  const { ref, inView } = useInViewOnce(0.2);
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const to = Number.isFinite(value) ? value : 0;

    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, durationMs]);

  const display = useMemo(() => {
    const fixed = decimals > 0 ? n.toFixed(decimals) : Math.round(n).toString();
    return `${fixed}${suffix}`;
  }, [n, decimals, suffix]);

  return (
    <Box ref={ref as any}>
      <Typography
        sx={{
          fontWeight: 1000,
          color: BRAND.ink,
          lineHeight: 1.05,
          fontFamily: "Poppins",
          fontSize,
        }}
      >
        {display}
      </Typography>
    </Box>
  );
};

/**
 * Rugby throw animation — pure SVG (no external assets).
 * Ball moves across, spins, and leaves a subtle trail.
 */
const RugbyThrowAnimation: React.FC = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: 140, sm: 170 },
        borderRadius: 4,
        border: `1px solid ${BRAND.border}`,
        background: `linear-gradient(180deg, rgba(180,212,255,0.55) 0%, rgba(238,245,255,0.9) 60%, #FFFFFF 100%)`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <svg viewBox="0 0 900 260" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="ballGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#F7F7F7" />
            <stop offset="100%" stopColor="#EAEAEA" />
          </linearGradient>

          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.16" />
          </filter>

          <path
            id="throwPath"
            d="M -120 190 C 140 30, 340 40, 520 110 C 670 170, 780 170, 1020 80"
            fill="none"
          />

          <linearGradient id="trailGrad" x1="0" x2="1">
            <stop offset="0%" stopColor={BRAND.accent} stopOpacity="0.0" />
            <stop offset="65%" stopColor={BRAND.accent} stopOpacity="0.12" />
            <stop offset="100%" stopColor={BRAND.accent} stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* subtle “field” lines */}
        <g opacity="0.35">
          {Array.from({ length: 7 }).map((_, i) => (
            <line
              key={i}
              x1={0}
              x2={900}
              y1={210 + i * 10}
              y2={210 + i * 10}
              stroke={BRAND.border}
              strokeWidth={1}
            />
          ))}
        </g>

        {/* trail */}
        <path
          d="M -120 190 C 140 30, 340 40, 520 110 C 670 170, 780 170, 1020 80"
          stroke="url(#trailGrad)"
          strokeWidth="14"
          strokeLinecap="round"
          fill="none"
          opacity="0.9"
        />

        {/* moving ball */}
        <g filter="url(#softShadow)">
          <g>
            <animateMotion dur="2.8s" repeatCount="indefinite" rotate="auto">
              <mpath href="#throwPath" />
            </animateMotion>

            {/* spin */}
            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 0 0"
                to="360 0 0"
                dur="0.9s"
                repeatCount="indefinite"
              />

              {/* ball shape */}
              <ellipse cx="0" cy="0" rx="42" ry="26" fill="url(#ballGrad)" stroke="#D8D8D8" strokeWidth="2" />
              {/* seams */}
              <path
                d="M -28 0 C -12 -10, 12 -10, 28 0 C 12 10, -12 10, -28 0 Z"
                fill="none"
                stroke="#C7C7C7"
                strokeWidth="2"
              />
              <line x1="-12" y1="-12" x2="-12" y2="12" stroke="#C7C7C7" strokeWidth="2" />
              <line x1="12" y1="-12" x2="12" y2="12" stroke="#C7C7C7" strokeWidth="2" />
            </g>
          </g>
        </g>
      </svg>
    </Box>
  );
};

// --- New: section transition (nice break between sections) ---
const SectionBreak: React.FC<{ label: string; subtitle?: string }> = ({ label, subtitle }) => (
  <Box sx={{ width: "100%", mt: { xs: 3.0, sm: 4.5 }, mb: { xs: 2.2, sm: 3.2 } }}>
    <Container maxWidth="lg">
      <Box
        sx={{
          position: "relative",
          borderRadius: 4,
          border: `1px solid ${BRAND.border}`,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${BRAND.panel} 0%, ${BRAND.bg} 55%, #FFFFFF 100%)`,
          boxShadow: "0 12px 26px rgba(23,107,135,0.10)",
          px: { xs: 2.2, sm: 3 },
          py: { xs: 2.0, sm: 2.4 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.18,
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(23,107,135,0.22) 0, rgba(23,107,135,0.0) 42%), radial-gradient(circle at 85% 65%, rgba(134,182,246,0.35) 0, rgba(134,182,246,0.0) 45%)",
          }}
        />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 99,
              backgroundColor: BRAND.accent,
              boxShadow: "0 8px 18px rgba(23,107,135,0.25)",
            }}
          />
          <Box sx={{ minWidth: 220 }}>
            <Typography fontFamily="Poppins" fontWeight={1000} sx={{ color: BRAND.ink, lineHeight: 1.1 }}>
              {label}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.70)", mt: 0.4 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Chip
            icon={<PublicOutlinedIcon sx={{ color: `${BRAND.accent} !important` }} />}
            label={`${DATASET_META.startYear} → ${DATASET_META.endYear}`}
            sx={{
              bgcolor: BRAND.white,
              border: `1px solid ${BRAND.border}`,
              fontWeight: 900,
              color: BRAND.accent,
              px: 0.5,
            }}
          />
        </Box>
      </Box>
    </Container>
  </Box>
);

// --- New: dataset overview section ---
const DatasetOverview: React.FC = () => {
  const spanYears = DATASET_META.endYear - DATASET_META.startYear + 1;

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 0.5, sm: 1 }, mb: { xs: 5, sm: 6.5 } }}>
      <Grid container spacing={3}>
        {/* Left: narrative + KPI tiles */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              backgroundColor: BRAND.white,
              borderRadius: 4,
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 14px 30px rgba(23,107,135,0.12)",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2.2, sm: 2.8 },
                py: { xs: 1.8, sm: 2.1 },
                backgroundColor: BRAND.panel,
                borderBottom: `1px solid ${BRAND.border}`,
              }}
            >
              <Typography fontFamily="Poppins" fontWeight={1000} sx={{ color: BRAND.ink }}>
                Dataset overview
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", mt: 0.4 }}>
                Match-level history spanning modern and early men's international rugby.
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2.2, sm: 2.8 } }}>
              <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", lineHeight: 1.75 }}>
                This dashboard is built around a simple idea: each row is one match. With consistent columns
                (teams, scores, competition, and venue context), we can power year-by-year trends, head-to-head
                comparisons, and “what changed?” style insights.
              </Typography>

              <Divider sx={{ my: 1.8, borderColor: "rgba(134,182,246,0.6)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 3,
                    border: `1px solid ${BRAND.border}`,
                    background: BRAND.soft,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                    <AccessTimeOutlinedIcon sx={{ color: BRAND.accent }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 1000, color: "rgba(11,34,48,0.72)" }}>
                      Time span
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: "Poppins", fontWeight: 1000, color: BRAND.ink, fontSize: 22 }}>
                    {DATASET_META.startYear}–{DATASET_META.endYear}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.68)", mt: 0.3 }}>
                    {spanYears} years of matches
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.4,
                    borderRadius: 3,
                    border: `1px solid ${BRAND.border}`,
                    background: BRAND.soft,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                    <TableViewOutlinedIcon sx={{ color: BRAND.accent }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 1000, color: "rgba(11,34,48,0.72)" }}>
                      Core fields
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontFamily: "Poppins", fontWeight: 1000, color: BRAND.ink, fontSize: 22 }}>
                    {DATASET_META.coreColumns.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.68)", mt: 0.3 }}>
                    columns powering charts
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1.8, borderColor: "rgba(134,182,246,0.6)" }} />

              <Typography
                sx={{ fontSize: 12, fontWeight: 1000, color: "rgba(11,34,48,0.72)", mb: 1.0 }}
                fontFamily="Poppins"
              >
                Teams Preview
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {DATASET_TEAMS_PREVIEW.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    sx={{
                      bgcolor: BRAND.white,
                      border: `1px solid ${BRAND.border}`,
                      fontWeight: 900,
                      color: BRAND.ink,
                    }}
                  />
                ))}
              </Stack>

            </CardContent>
          </Card>
        </Grid>

        {/* Right: schema + sample rows */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              backgroundColor: BRAND.white,
              borderRadius: 4,
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 14px 30px rgba(23,107,135,0.12)",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2.2, sm: 2.8 },
                py: { xs: 1.8, sm: 2.1 },
                backgroundColor: BRAND.panel,
                borderBottom: `1px solid ${BRAND.border}`,
              }}
            >
              <Typography fontFamily="Poppins" fontWeight={1000} sx={{ color: BRAND.ink }}>
                Schema + sample
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", mt: 0.4 }}>
                The same structure holds from early records (1871) to modern matches (2022).
              </Typography>
            </Box>

            <CardContent sx={{ p: { xs: 2.2, sm: 2.8 } }}>
              {/* Schema chips */}
              <Box
                sx={{
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 3,
                  overflow: "hidden",
                  background: BRAND.soft,
                }}
              >
                <Box
                  sx={{
                    px: 1.6,
                    py: 1.1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderBottom: `1px solid ${BRAND.border}`,
                    backgroundColor: "rgba(255,255,255,0.55)",
                  }}
                >
                  <CategoryOutlinedIcon sx={{ color: BRAND.accent }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 1000, color: BRAND.ink }} fontFamily="Poppins">
                    Core columns (used by filters & charts)
                  </Typography>
                </Box>

                <Box sx={{ p: 1.6 }}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {DATASET_META.coreColumns.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        sx={{
                          bgcolor: BRAND.white,
                          border: `1px solid ${BRAND.border}`,
                          fontWeight: 900,
                          color: BRAND.accent,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>

              <Divider sx={{ my: 2.0, borderColor: "rgba(134,182,246,0.6)" }} />

              {/* Sample block */}
              <Box
                sx={{
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 3,
                  overflow: "hidden",
                  backgroundColor: "#0B2230",
                }}
              >
                <Box
                  sx={{
                    px: 1.4,
                    py: 1.0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 1000, color: "rgba(255,255,255,0.88)" }}>
                    Sample rows (CSV)
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    label="example"
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.85)" }}
                  />
                </Box>

                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    fontSize: 12.5,
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.88)",
                  }}
                >
                  {DATASET_SAMPLE}
                </Box>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

const Home: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: BRAND.bg,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Hero / Brand bar */}
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          background: `linear-gradient(135deg, ${BRAND.panel} 0%, ${BRAND.bg} 60%)`,
          borderBottom: `1px solid ${BRAND.border}`,
          py: { xs: 3.5, sm: 4.5 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
              gap: { xs: 2.5, md: 3 },
              alignItems: "center",
            }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                variant="h3"
                fontWeight={900}
                fontFamily="Poppins"
                sx={{ color: BRAND.ink, letterSpacing: 0.2 }}
              >
                RugbyRadar
              </Typography>

              {/* Typed hero subtitle */}
              <Typography variant="h6" fontFamily="Poppins" sx={{ color: "rgba(11,34,48,0.75)", mt: 0.8 }}>
                <TypedText
                  sequence={[
                    "From Pitch to Pattern.",
                    900,
                    "Spot trends. Compare teams. Learn faster.",
                    900,
                    "Make match data feel obvious.",
                    900,
                  ]}
                  speed={55}
                  repeat={Infinity}
                />
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                justifyContent={{ xs: "center", md: "flex-start" }}
                sx={{ mt: 2.0, flexWrap: "wrap", rowGap: 1 }}
              >
                <Chip
                  label="Trends"
                  sx={{
                    bgcolor: BRAND.white,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 800,
                    color: BRAND.accent,
                  }}
                />
                <Chip
                  label="Team Performance"
                  sx={{
                    bgcolor: BRAND.white,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 800,
                    color: BRAND.accent,
                  }}
                />
                <Chip
                  label="Head-to-Head"
                  sx={{
                    bgcolor: BRAND.white,
                    border: `1px solid ${BRAND.border}`,
                    fontWeight: 800,
                    color: BRAND.accent,
                  }}
                />
              </Stack>
            </Box>

            {/* Right hero card */}
            <Card
              sx={{
                backgroundColor: BRAND.white,
                borderRadius: 4,
                border: `1px solid ${BRAND.border}`,
                boxShadow: "0 14px 30px rgba(23,107,135,0.16)",
              }}
            >
              <CardContent sx={{ p: { xs: 2.2, sm: 2.8 } }}>
                <Typography fontFamily="Poppins" fontWeight={900} sx={{ color: BRAND.ink, mb: 1 }}>
                  What you get
                </Typography>

                <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", lineHeight: 1.7 }}>
                  RugbyRadar turns match data into clean, explorable visuals—so you can spot patterns,
                  compare teams, and understand what actually drives wins.
                </Typography>

                <Divider sx={{ my: 1.8, borderColor: "rgba(134,182,246,0.6)" }} />

                <Stack spacing={1.1}>
                  {[
                    "Matches per year trends (macro view)",
                    "Team performance tiles + opponent breakdowns",
                    "Head-to-head filters + margin trends",
                  ].map((t) => (
                    <Stack key={t} direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 99,
                          backgroundColor: BRAND.accent,
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)" }}>
                        {t}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* Animated "numbers" block */}
                <Box
                  sx={{
                    mt: 2,
                    p: 1.6,
                    borderRadius: 3,
                    border: `1px solid ${BRAND.border}`,
                    background: BRAND.soft,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.2,
                  }}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: "rgba(11,34,48,0.72)" }}>
                      Teams tracked
                    </Typography>
                    <AnimatedNumber value={10} durationMs={700} fontSize={30} />
                  </Box>

                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: "rgba(11,34,48,0.72)" }}>
                      Key views
                    </Typography>
                    <AnimatedNumber value={5} durationMs={700} fontSize={30} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Rugby throw animation strip */}
          <Box sx={{ mt: { xs: 2.5, md: 3 } }}>
            <RugbyThrowAnimation />
          </Box>
        </Container>
      </Box>

      {/* Intro blurb */}
      <Container
        maxWidth="md"
        sx={{
          width: { xs: "92%", sm: "86%", md: "80%" },
          mt: { xs: 3.5, sm: 5 },
        }}
      >
        <Card
          sx={{
            backgroundColor: BRAND.white,
            borderRadius: 4,
            border: `1px solid ${BRAND.border}`,
            boxShadow: "0 12px 26px rgba(23,107,135,0.12)",
          }}
        >
          <CardContent sx={{ px: { xs: 2.2, sm: 3.5 }, py: { xs: 2.4, sm: 3.2 } }}>
            <Typography
              variant="h6"
              sx={{ color: BRAND.accent, textAlign: "center", lineHeight: 1.8 }}
              fontFamily="Poppins"
              fontWeight={800}
            >
              RugbyRadar turns rugby matches into engaging visuals—spotting patterns, team performance,
              and win–loss dynamics—so players, coaches, and fans get a sharper read on the game.
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* NEW: Transition + Dataset Overview */}
      <SectionBreak label="Data you’re exploring" subtitle="A match-by-match history that stretches from the earliest internationals to the modern era." />
      <DatasetOverview />

      {/* Features */}
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 5, sm: 7 } }}>
        <Typography
          variant="h5"
          textAlign="center"
          fontWeight={900}
          sx={{ color: BRAND.ink, mb: 3.5 }}
          fontFamily="Poppins"
        >
          Features
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA banner */}
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          background: `linear-gradient(135deg, ${BRAND.accent} 0%, #0F5268 100%)`,
          color: BRAND.white,
          textAlign: "center",
          py: { xs: 3, sm: 3.5 },
          borderTop: "1px solid rgba(255,255,255,0.18)",
          borderBottom: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <Typography variant="h4" fontWeight={900} fontFamily="Poppins">
          <TypedText
            sequence={["Track Every Try", 1200, "Track Every Phase", 1200, "Track Every Try"]}
            speed={65}
            repeat={Infinity}
          />
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }} fontFamily="Poppins">
          <TypedText
            sequence={["Analyze Every Team", 1200, "Understand Every Pattern", 1200, "Analyze Every Team"]}
            speed={65}
            repeat={Infinity}
          />
        </Typography>
      </Box>

      {/* Explore cards */}
      <Container maxWidth="lg" sx={{ mt: { xs: 4, sm: 6 }, mb: { xs: 5, sm: 7 } }}>
        <Typography
          variant="h5"
          textAlign="center"
          fontWeight={900}
          sx={{ color: BRAND.ink, mb: 3.5 }}
          fontFamily="Poppins"
        >
          Explore
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {ctaFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Glossary */}
      <Container maxWidth="lg" sx={{ mb: { xs: 7, sm: 9 } }}>
        <Box
          sx={{
            backgroundColor: BRAND.white,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            boxShadow: "0 14px 30px rgba(23,107,135,0.14)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.2, sm: 3 },
              py: { xs: 2.0, sm: 2.4 },
              backgroundColor: BRAND.panel,
              borderBottom: `1px solid ${BRAND.border}`,
            }}
          >
            <Typography fontFamily="Poppins" fontWeight={900} sx={{ color: BRAND.ink }}>
              Rugby Glossary
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(11,34,48,0.72)", mt: 0.4 }}>
              Quick definitions for the most common rugby terms you’ll need to know.
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2.2, sm: 3 }, py: { xs: 2.2, sm: 3 } }}>
            <Grid container spacing={2.2}>
              {glossary.map((g) => (
                <Grid item xs={12} sm={6} md={4} key={g.term}>
                  <GlossaryCard {...g} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
